import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useCallback, useEffect, useRef, memo, useState } from 'react';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.string.match.js';
import 'core-js/modules/es.promise.js';
import { ErrorBoundary } from 'react-error-boundary';
import 'core-js/modules/es.regexp.to-string.js';
import 'core-js/modules/es.symbol.description.js';
import { proxy, subscribe, useSnapshot } from 'valtio';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const scriptsCache = {};
const cacheBustModifier = Math.random() * 1000 + Math.random() * 100 + Math.random() * 10 + Math.random();
//@ts-ignore
const deferred = args => {
  args = args || {};
  const {
    timeout
  } = args;
  //@ts-ignore
  let resolve;
  //@ts-ignore
  let reject;
  //@ts-ignore
  let timeoutId;
  const promise = new Promise((resolver, rejector) => {
    resolve = resolver;
    reject = rejector;
  });
  //@ts-ignore
  promise.resolve = arg => {
    //@ts-ignore
    clearTimeout(timeoutId);
    //@ts-ignore
    resolve(arg);
  };
  //@ts-ignore
  promise.reject = arg => {
    //@ts-ignore
    clearTimeout(timeoutId);
    //@ts-ignore
    reject(arg);
  };
  if (typeof timeout === 'number') {
    const id = args.id || 'anonymous deferred';
    timeoutId = setTimeout(
    //@ts-ignore
    () => reject({
      reason: `timeout (${timeout}) reached on "${id}"`
    }), timeout);
  }
  return promise;
};
//@ts-ignore
const loadJS = src => __awaiter(void 0, void 0, void 0, function* () {
  //@ts-ignore
  if (scriptsCache[src]) {
    //@ts-ignore
    return scriptsCache[src];
  }
  const dfd = deferred({
    timeout: 30000
  });
  const {
    document: doc
  } = window;
  const script = doc.createElement('script');
  script.setAttribute('type', 'text/javascript');
  script.setAttribute('src', `${src}?cb=${cacheBustModifier}`);
  script.addEventListener('error', err => {
    //@ts-ignore
    scriptsCache[src] = null;
    dfd.reject(err);
  });
  script.addEventListener('load', () => {
    dfd.resolve(script);
  });
  doc.head.appendChild(script);
  //@ts-ignore
  scriptsCache[src] = dfd;
  return dfd;
});

//@ts-ignore
const loadRemote = remote => __awaiter(void 0, void 0, void 0, function* () {
  const {
    name,
    url
  } = remote;
  yield loadJS(url);
  // Initializes the share scope. This fills it with known provided modules from this build and all remotes
  // @ts-ignore
  yield __webpack_init_sharing__('default');
  const container = window[name]; // or get the container somewhere else
  if (!container || !container.init) throw new Error(`Cannot load external remote: ${name} from url: ${url}`);
  // @ts-ignore
  // Initialize the container, it may provide shared modules
  yield container.init(__webpack_share_scopes__.default);
  // console.log("__webpack_share_scopes__", __webpack_share_scopes__);
  return container;
});
// @ts-ignore
const loadComponent = ({
  remote: _remote = {},
  component
} = {}) => {
  // @ts-ignore
  const {
    name,
    url
  } = _remote;
  if (!url) throw new Error('Missing remote url');
  if (!name) throw new Error('Missing remote name');
  if (!component) throw new Error('Missing component');
  return () => __awaiter(void 0, void 0, void 0, function* () {
    let container;
    try {
      container = yield loadRemote({
        url,
        name
      });
    } catch (error) {
      throw new Error(`Cannot load external remote: ${name} from url: ${url}`);
    }
    if (!container.get) throw new Error(`Cannot load external remote: ${name} from url: ${url}`);
    component = component.match(/^\.\//) ? component : `./${component}`;
    const factory = yield container.get(component);
    if (!factory) throw new Error(`Cannot load ${component} in remote: ${name} from url ${url}`);
    return factory();
  });
};

function transformEnvironmentString(envString) {
  switch (envString.toLowerCase()) {
    case 'development':
    case 'dev':
    case 'test':
      return 'dev';
    case 'stage':
    case 'stg':
      return 'stg';
    case 'production':
    case 'prd':
    case 'prod':
      return 'prd';
    case 'local':
      return 'local';
    default:
      return envString;
  }
}

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

const initialUniversalStateObject = {
  language: 'en-us',
  environment: undefined,
  isLoggedIn: false,
  tileRegistryUri: undefined,
  user: undefined,
  notifications: []
};
const UniversalContextDefaultStateProxy = proxy(initialUniversalStateObject);
const universalStateSubscribe = callback => {
  return subscribe(UniversalContextDefaultStateProxy, () => callback(UniversalContextDefaultStateProxy));
};
const CART_REFRESH_EVENT = 'cartRefresh';
const CART_STORAGE_KEY = 'cart';
const SCOPEDCONTEXT_STORAGEKEY = 'scoped-context';
const UNIVERSALCONTEXT_STORAGEKEY = 'universal-context';
const UNIVERSALCONTEXTUPDATE = 'universal-context-update';
const UNIVERSALCONTEXTPUSH = 'universal-context-push';
const UNIVERSALCONTEXTSCOPEDPROPERTYUPDATE = 'universal-context-scoped-property-update';
const UNIVERSALCONTEXTSCOPEDPROPERTYPUSH = 'universal-context-scoped-property-push';
const UNIVERSALCONTEXT_REQUEST_ROUTER_NAVIGATE_EVENT = 'universal-context-request-router-navigate';
const validateCartItem = cartItem => {
  const stringTypeCheck = value => {
    return Object.prototype.toString.call(value) === '[object String]';
  };
  const intTypeCheck = value => {
    return Object.prototype.toString.call(value) === '[object Number]';
  };
  if (
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('id') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('name') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('imageUrls') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('price') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('description') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('quantity') ||
  // eslint-disable-next-line no-prototype-builtins
  !cartItem.hasOwnProperty('metadata')) {
    throw new Error('Invalid Cart item');
  }
  if (!stringTypeCheck(cartItem.id) || !stringTypeCheck(cartItem.name) || !Array.isArray(cartItem.imageUrls) || !intTypeCheck(cartItem.price) || !stringTypeCheck(cartItem.description) || !intTypeCheck(cartItem.quantity)) {
    throw new Error('Invalid Cart item');
  }
};
let existingScopedContextInStorage;
try {
  existingScopedContextInStorage = JSON.parse(sessionStorage.getItem(SCOPEDCONTEXT_STORAGEKEY));
  // eslint-disable-next-line no-empty
} catch (error) {}
const scopedContextProxy = proxy([...new Array(), ...(existingScopedContextInStorage || [])]);
let existingContextInStorage;
try {
  existingContextInStorage = JSON.parse(sessionStorage.getItem(UNIVERSALCONTEXT_STORAGEKEY));
} catch (error) {
  existingContextInStorage = null;
}
const contextProxy = proxy(Object.assign(Object.assign({}, initialUniversalStateObject), existingContextInStorage));
const useUniversalContext = state => {
  if (state) {
    Object.keys(state).forEach(key => {
      if (existingContextInStorage && existingContextInStorage[key]) {
        contextProxy[key] = existingContextInStorage[key];
      } else {
        contextProxy[key] = state[key];
      }
    });
  }
  const {
    language,
    environment,
    isLoggedIn,
    tileRegistryUri,
    user,
    notifications
  } = useSnapshot(contextProxy);
  const broadcastUniversalContextUpdate = useCallback((propertyName, propertyValue) => {
    const payload = {
      language,
      environment,
      isLoggedIn,
      user,
      tileRegistryUri,
      [propertyName]: propertyValue
    };
    sessionStorage.setItem(UNIVERSALCONTEXT_STORAGEKEY, JSON.stringify(payload));
    const event = new CustomEvent(UNIVERSALCONTEXTUPDATE, {
      detail: payload
    });
    document.dispatchEvent(event);
  }, [language, environment, isLoggedIn, user, tileRegistryUri]);
  useEffect(() => {
    const silentUpdateProperty = (propertyName, propertyValue) => {
      contextProxy[propertyName] = propertyValue;
      broadcastUniversalContextUpdate(propertyName, propertyValue);
    };
    document.addEventListener(UNIVERSALCONTEXTPUSH, e => silentUpdateProperty(e.detail.propertyName, e.detail.propertyValue));
    return document.removeEventListener(UNIVERSALCONTEXTPUSH, e => silentUpdateProperty(e.detail.propertyName, e.detail.propertyValue));
  }, [broadcastUniversalContextUpdate]);
  //setters
  const setIsLoggedIn = isLoggedIn => {
    contextProxy.isLoggedIn = isLoggedIn;
    broadcastUniversalContextUpdate('isLoggedIn', isLoggedIn);
    return isLoggedIn;
  };
  const setEnvironment = environment => {
    contextProxy.environment = transformEnvironmentString(environment);
    broadcastUniversalContextUpdate('environment', environment);
    return contextProxy.environment;
  };
  const setLanguage = language => {
    contextProxy.language = language;
    broadcastUniversalContextUpdate('language', language);
    return language;
  };
  const setTileRegistryUri = tileRegistryUri => {
    contextProxy.tileRegistryUri = tileRegistryUri;
    broadcastUniversalContextUpdate('tileRegistryUri', tileRegistryUri);
    return tileRegistryUri;
  };
  const setPropertyValue = (propertyName, propertyValue) => {
    contextProxy[propertyName] = propertyValue;
    broadcastUniversalContextUpdate(propertyName, propertyValue);
  };
  const setUser = user => {
    contextProxy.user = user;
    broadcastUniversalContextUpdate('user', user);
    return user;
  };
  const addNotification = notification => {
    contextProxy.notifications = [...contextProxy.notifications, Object.assign(Object.assign({}, notification), {
      id: v4()
    })];
    broadcastUniversalContextUpdate('notifications', contextProxy.notifications);
    return notifications;
  };
  const removeNotification = id => {
    contextProxy.notifications = [...contextProxy.notifications.filter(notification => notification.id !== id)];
    broadcastUniversalContextUpdate('notifications', contextProxy.notifications);
    return notifications;
  };
  const getCartItems = useCallback(() => {
    const cartItems = localStorage.getItem(CART_STORAGE_KEY);
    return cartItems ? JSON.parse(cartItems) : [];
  }, []);
  const addToCart = useCallback(item => {
    validateCartItem(item);
    const updatedCart = [...getCartItems(), item];
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, {
      detail: updatedCart
    }));
  }, [getCartItems]);
  const getCartItemById = useCallback(itemId => {
    return getCartItems().find(item => item.id === itemId);
  }, [getCartItems]);
  const removeCartItemById = useCallback(itemId => {
    const updatedCart = [...getCartItems().filter(item => item.id !== itemId)];
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, {
      detail: updatedCart
    }));
  }, [getCartItems]);
  const updateCartItemById = useCallback((updatedItemId, payload) => {
    validateCartItem(payload);
    let updatedCartItem = getCartItems().find(item => item.id === updatedItemId);
    updatedCartItem = Object.assign(Object.assign({}, updatedCartItem), payload);
    const updatedCart = getCartItems().map(item => {
      if (item.id === updatedItemId) {
        return updatedCartItem;
      }
      return item;
    });
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, {
      detail: updatedCart
    }));
  }, [getCartItems]);
  const clearCart = useCallback(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify([]));
    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, {
      detail: []
    }));
  }, []);
  const scopedContextSnapShot = useSnapshot(scopedContextProxy);
  const setScopedValue = useCallback((scope, propertyName, propertyValue, isScopedToUser) => {
    //find the context or create it
    const propertyScope = scopedContextProxy.find(sp => sp.scope === scope);
    if (!propertyScope) {
      scopedContextProxy.push({
        scope,
        isScopedToUser,
        properties: [{
          name: propertyName,
          value: propertyValue
        }]
      });
    } else {
      //it already exists, so update
      let scopedProperty = propertyScope.properties.find(v => v.name === propertyName);
      if (!scopedProperty) {
        scopedProperty = {
          name: propertyName,
          value: propertyValue
        };
        propertyScope.properties.push(scopedProperty);
      }
      scopedProperty.value = propertyValue;
    }
    sessionStorage.setItem(SCOPEDCONTEXT_STORAGEKEY, JSON.stringify(scopedContextProxy));
    const event = new CustomEvent(UNIVERSALCONTEXTSCOPEDPROPERTYUPDATE, {
      detail: {
        scope,
        propertyName,
        propertyValue,
        isScopedToUser,
        scopedContext: JSON.parse(JSON.stringify(scopedContextProxy))
      }
    });
    document.dispatchEvent(event);
  }, []);
  useEffect(() => {
    document.addEventListener(UNIVERSALCONTEXTSCOPEDPROPERTYPUSH, e => {
      var _a;
      return setScopedValue(e.detail.scope, e.detail.propertyName, e.detail.propertyValue, (_a = e.detail) === null || _a === void 0 ? void 0 : _a.isScopedToUser);
    });
    return document.removeEventListener(UNIVERSALCONTEXTSCOPEDPROPERTYPUSH, e => {
      var _a;
      return setScopedValue(e.detail.scope, e.detail.propertyName, e.detail.propertyValue, (_a = e.detail) === null || _a === void 0 ? void 0 : _a.isScopedToUser);
    });
  }, [setScopedValue]);
  const subscribeScopedValue = useCallback((scope, propertyName) => {
    const scopedContext = scopedContextSnapShot.find(sp => sp.scope === scope);
    if (!scopedContext) {
      return null;
    }
    const scopedProperty = scopedContext.properties.find(v => v.name === propertyName);
    if (!scopedProperty) {
      return null;
    }
    return scopedProperty.value;
  }, [scopedContextSnapShot]);
  /*
  Method to clear the scoped properties.
  This method will clear all scoped properties unless a scope is specified in which case only properties for that scope will be cleared.
   */
  const clearScopedValues = useCallback(scope => {
    //if you don't specify scope, remove everything
    if (!scope) {
      scopedContextProxy.forEach((context, index, array) => array.pop());
    } else {
      //remove only the properties on the specified scope
      const scopedContext = scopedContextProxy.find(sp => sp.scope === scope);
      if (!scopedContext) {
        return;
      }
      scopedContext.properties = [];
    }
    sessionStorage.setItem(SCOPEDCONTEXT_STORAGEKEY, JSON.stringify(scopedContextProxy));
    const event = new CustomEvent(UNIVERSALCONTEXTSCOPEDPROPERTYUPDATE, {
      detail: {
        scope,
        scopedContext: JSON.parse(JSON.stringify(scopedContextProxy))
      }
    });
    document.dispatchEvent(event);
  }, []);
  /*
  Method to clear all isScopedToUser scoped properties.
  This method will clear all scoped properties where the property isScopedToUser equals true on the scoped property.
  */
  const clearScopedToUserProperties = useCallback(() => {
    scopedContextProxy.forEach((context, index, array) => {
      if (context.isScopedToUser) {
        array.splice(index, 1);
      }
    });
    sessionStorage.setItem(SCOPEDCONTEXT_STORAGEKEY, JSON.stringify(scopedContextProxy));
    const event = new CustomEvent(UNIVERSALCONTEXTUPDATE, {
      detail: {
        clearScopedToUserProperties: true,
        scopedContext: JSON.parse(JSON.stringify(scopedContextProxy))
      }
    });
    document.dispatchEvent(event);
  }, []);
  /*
  Broadcast a navigation request
  */
  const navigate = useCallback((to, replace, state) => {
    const event = new CustomEvent(UNIVERSALCONTEXT_REQUEST_ROUTER_NAVIGATE_EVENT, {
      detail: {
        to,
        replace,
        state
      }
    });
    document.dispatchEvent(event);
  }, []);
  /*
  Broadcast a caught error
  */
  const reportError = useCallback(error => {
    const errorId = v4();
    //Emit a notification upon errors being reported (only if it's a non-view error)
    if (!error.viewContext) {
      const errorNotification = {
        id: errorId,
        variant: 'error',
        body: error.errorPayload
      };
      contextProxy.notifications = [...contextProxy.notifications, errorNotification];
    }
  }, [setScopedValue]);
  return {
    setIsLoggedIn,
    isLoggedIn,
    setEnvironment,
    environment,
    setLanguage,
    language,
    setTileRegistryUri,
    tileRegistryUri,
    setPropertyValue,
    setScopedValue,
    subscribeScopedValue,
    clearScopedValues,
    clearScopedToUserProperties,
    addToCart,
    getCartItems,
    getCartItemById,
    removeCartItemById,
    updateCartItemById,
    clearCart,
    user: user,
    setUser,
    notifications,
    addNotification,
    removeNotification,
    CART_REFRESH_EVENT,
    navigate,
    UNIVERSALCONTEXT_REQUEST_ROUTER_NAVIGATE_EVENT,
    reportError
  };
};

const UniversalContextProvider = ({
  children
}) => {
  const {
    setLanguage,
    language
  } = useUniversalContext();
  useEffect(() => {
    if (!language) {
      setLanguage(navigator.languages ? navigator.languages[0] : 'en-us');
    }
  }, [setLanguage, language]);
  return children;
};

const vueInstances = {};
const VueWrapperFC = ({
  componentOrFunction,
  on,
  scriptPath,
  uniqueId
}) => {
  /**
   * ||=========================
   * || Use universal context makes sure
   * || the component is reactive
   */
  const universalContext = useUniversalContext();
  const ref = useRef(null);
  const [vueLibLoaded, setVueLibLoaded] = React.useState(typeof Vue !== 'undefined');
  useEffect(() => {
    if (typeof window !== 'undefined' && !vueLibLoaded) {
      if (typeof Vue === 'undefined') {
        loadJS(scriptPath !== null && scriptPath !== void 0 ? scriptPath : 'https://cdn.jsdelivr.net/npm/vue@2.6.14').then(() => setVueLibLoaded(true));
      }
    }
  }, [scriptPath, vueLibLoaded, setVueLibLoaded]);
  useEffect(() => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    //wait until Vue library is loaded
    if (!vueLibLoaded) {
      return;
    }
    //initialize pseudo-ref
    // @ts-ignore
    if (!vueInstances[uniqueId]) {
      // @ts-ignore
      vueInstances[uniqueId] = {
        current: null,
        renderCount: 0
      };
    }
    //if psuedo-ref isn't set, load the Vue instance into ref
    // @ts-ignore
    if (!vueInstances[uniqueId].current) {
      const data = {
        universalContext: {
          setPropertyValue: universalContext.setPropertyValue,
          setScopedValue: universalContext.setScopedValue,
          subscribeScopedValue: universalContext.subscribeScopedValue,
          name: (universalContext === null || universalContext === void 0 ? void 0 : universalContext.user) ? `${(_b = (_a = universalContext === null || universalContext === void 0 ? void 0 : universalContext.user) === null || _a === void 0 ? void 0 : _a.firstName) === null || _b === void 0 ? void 0 : _b.toString()} ${(_d = (_c = universalContext === null || universalContext === void 0 ? void 0 : universalContext.user) === null || _c === void 0 ? void 0 : _c.lastName) === null || _d === void 0 ? void 0 : _d.toString()}` : '',
          email: (_f = (_e = universalContext === null || universalContext === void 0 ? void 0 : universalContext.user) === null || _e === void 0 ? void 0 : _e.email) === null || _f === void 0 ? void 0 : _f.toString(),
          userId: (_h = (_g = universalContext === null || universalContext === void 0 ? void 0 : universalContext.user) === null || _g === void 0 ? void 0 : _g.userId) === null || _h === void 0 ? void 0 : _h.toString(),
          environment: (_j = universalContext.environment) === null || _j === void 0 ? void 0 : _j.toString(),
          language: (_k = universalContext.language) === null || _k === void 0 ? void 0 : _k.toString(),
          isLoggedIn: universalContext.isLoggedIn === true,
          navigate: universalContext.navigate
        },
        // @ts-ignore
        renderCount: ++vueInstances[uniqueId].renderCount
      };
      // @ts-ignore
      vueInstances[uniqueId].current = isRenderFunction(componentOrFunction) ? componentOrFunction(data) : new Vue({
        data,
        // @ts-ignore
        render(ce) {
          return ce(uniqueId, {
            props: this.$data,
            on
          });
        },
        components: {
          [uniqueId]: componentOrFunction
        }
      });
    }
    //mount the referenced Vue instance to the div
    // @ts-ignore
    Promise.resolve(vueInstances[uniqueId].current).then(function (instance) {
      // @ts-ignore
      vueInstances[uniqueId].current = instance;
      // @ts-ignore
      vueInstances[uniqueId].current.$mount(ref.current);
    });
    //unmount when the VueWrapper is destroyed
    return function unmount() {
      // @ts-ignore
      Promise.resolve(vueInstances[uniqueId].current).then(instance => {
        instance.$destroy();
        // @ts-ignore
        vueInstances[uniqueId].current = null;
      });
    };
  }, [vueLibLoaded, componentOrFunction, universalContext, on, uniqueId]);
  return jsx("div", {
    ref: ref
  });
};
function isRenderFunction(componentOrFunction) {
  return typeof componentOrFunction == 'function';
}

// import { incrCount } from "../../reducer.actions";
const classes = {
  ALERT: 'cxc-tile__error-card-alert',
  CARD: 'cxc-tile__error-card',
  CONTENT: 'cxc-tile__error-card-content',
  SKELETON: 'cxc-tile__skeleton'
};

const angularInstances = {};
const AngularWrapper = ({
  component,
  tileComponentData
}) => {
  const angularRef = useRef(null);
  useEffect(() => {
    //initialize pseudo-ref
    if (!angularInstances[tileComponentData.viewId]) {
      angularInstances[tileComponentData.viewId] = {
        current: null,
        renderCount: 0
      };
    }
    //if psuedo-ref isn't set, load the Angular instance into ref
    if (!angularInstances[tileComponentData.viewId].current) {
      angularInstances[tileComponentData.viewId].current = component;
      //mount the referenced Angular instance to the div
      Promise.resolve(angularInstances[tileComponentData.viewId].current).then(function (instance) {
        angularInstances[tileComponentData.viewId].current = instance;
        if (angularInstances[tileComponentData.viewId].renderCount <= 1) {
          angularInstances[tileComponentData.viewId].current.mount(); //mount is exported from module
        }
      });
      //unmount when the AngularWrapper is destroyed
      return function unmount() {
        Promise.resolve(angularInstances[tileComponentData.viewId].current).then(() => {
          angularInstances[tileComponentData.viewId].current = null;
        });
      };
    }
    return;
  }, [component, tileComponentData.viewId]);
  return jsx(component.selector, {
    ref: angularRef
  });
};
AngularWrapper.displayName = 'AngularWrapper';
const MemoizedAngularWrapper = /*#__PURE__*/memo(AngularWrapper, (p, n) => {
  return p.tileComponentData['viewId'] === n.tileComponentData['viewId'];
});
MemoizedAngularWrapper.displayName = 'MemoizedAngularWrapper';

const BaseTile = ({
  tileData,
  errorComponent: ErrorComponent
}) => {
  const [loading, setLoading] = useState(true);
  const [output, setOutput] = useState();
  const ErrorFallbackWrapper = React.useCallback(props => jsx(ErrorComponent, Object.assign({}, props)), [ErrorComponent]);
  useEffect(() => {
    const formatTileData = data => {
      if (data) {
        return {
          remote: {
            url: data.uri,
            name: data.name
          },
          component: data.exposedComponent,
          tileType: data.tileType,
          viewId: data.id
        };
      } else {
        throw new Error('invalid tile data received');
      }
    };
    const getDynamicReactComponent = tileComponentData => {
      const DynamicReactComponent = /*#__PURE__*/React.lazy(loadComponent(tileComponentData));
      return jsx(React.Suspense, Object.assign({
        fallback: ``
      }, {
        children: jsx(DynamicReactComponent, {})
      }));
    };
    const getDynamicVueComponent = tileComponentData => __awaiter(void 0, void 0, void 0, function* () {
      try {
        const VueComponentOrFunction = yield loadComponent(tileComponentData)();
        const DynamicVueComponent = jsx(VueWrapperFC, {
          componentOrFunction: VueComponentOrFunction.default,
          uniqueId: `${tileComponentData.viewId}`
        });
        return DynamicVueComponent;
      } catch (error) {
        return jsx(ErrorFallbackWrapper, {
          error: error
        });
      }
    });
    const getDynamicAngularComponent = tileComponentData => __awaiter(void 0, void 0, void 0, function* () {
      try {
        const AngularComponent = yield loadComponent(tileComponentData)();
        const DynamicAngularComponent = jsx(MemoizedAngularWrapper, {
          tileComponentData: tileComponentData,
          component: AngularComponent
        });
        return DynamicAngularComponent;
      } catch (error) {
        return jsx(ErrorFallbackWrapper, {
          error: error
        });
      }
    });
    (() => __awaiter(void 0, void 0, void 0, function* () {
      if (tileData) {
        const formattedTileData = formatTileData(tileData);
        let element;
        switch (formattedTileData.tileType) {
          case 'react':
            element = getDynamicReactComponent(formattedTileData);
            break;
          case 'vue':
            element = yield getDynamicVueComponent(formattedTileData);
            break;
          case 'angular':
            element = yield getDynamicAngularComponent(formattedTileData);
            break;
          case 'other':
            element = jsx("div", {
              children: "Please speak with FPG and CXC teams for assistance with implementation"
            });
            break;
          default:
            element = jsxs("div", {
              children: ["Unrecognized tile type: ", formattedTileData.tileType]
            });
            break;
        }
        setOutput(element);
        setLoading(false);
      }
    }))();
  }, [tileData, ErrorFallbackWrapper]);
  return loading === true ? jsx("div", {
    className: classes.SKELETON
  }) : jsx(ErrorBoundary, Object.assign({
    FallbackComponent: ErrorFallbackWrapper
  }, {
    children: output
  }));
};
const MemoizedBaseTile = /*#__PURE__*/memo(BaseTile, (p, n) => {
  return p.tileData.exposedComponent === n.tileData.exposedComponent;
});
MemoizedBaseTile.displayName = 'MemoizedBaseTile';

export { MemoizedBaseTile as BaseTile, UniversalContextDefaultStateProxy, UniversalContextProvider, initialUniversalStateObject, universalStateSubscribe, useUniversalContext };
